/**
 * Remote app config — the force-update kill switch, plus what rides along with it.
 *
 * Mirrors `src/modules/app-config` on the backend. One row per app × platform: the two
 * apps ship on their own cadence, and Android and iOS store rollouts move at different
 * speeds, so all four are armed independently.
 */

export type AppClient = 'CUSTOMER' | 'VENDOR';
export type AppPlatform = 'ANDROID' | 'IOS';
export type UpdateStatus = 'NONE' | 'OPTIONAL' | 'FORCED';

/** One stored row, as the admin endpoints return it. */
export type AppConfig = {
  id: string;
  app: AppClient;
  platform: AppPlatform;
  minimum_build: number;
  latest_build: number;
  latest_version: string;
  store_url: string;
  update_title: string;
  update_message: string;
  optional_update_title: string;
  optional_update_message: string;
  is_maintenance: boolean;
  maintenance_title: string;
  maintenance_message: string;
  support_email: string | null;
  support_phone: string | null;
  support_whatsapp: string | null;
  feature_flags: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

/** Every field optional — the usual edit is one number. */
export type UpdateAppConfigInput = Partial<
  Pick<
    AppConfig,
    | 'minimum_build'
    | 'latest_build'
    | 'latest_version'
    | 'store_url'
    | 'update_title'
    | 'update_message'
    | 'optional_update_title'
    | 'optional_update_message'
    | 'is_maintenance'
    | 'maintenance_title'
    | 'maintenance_message'
    | 'support_email'
    | 'support_phone'
    | 'support_whatsapp'
  >
>;

/** Exactly what a phone gets back from the public endpoint. */
export type PublicAppConfig = {
  app: {
    update_status: UpdateStatus;
    latest_version: string;
    latest_build: number;
    store_url: string;
    title: string | null;
    message: string | null;
  };
  maintenance: { is_active: boolean; title: string | null; message: string | null };
  support: { email: string | null; phone: string | null; whatsapp: string | null };
  feature_flags: Record<string, unknown>;
};

export const APP_LABEL: Record<AppClient, string> = {
  CUSTOMER: 'Customer app',
  VENDOR: 'Vendor app',
};

export const PLATFORM_LABEL: Record<AppPlatform, string> = {
  ANDROID: 'Android',
  IOS: 'iOS',
};

/** The URL segments the admin routes take — lowercase, matching the backend's parser. */
export const path_for = (app: AppClient, platform: AppPlatform): string =>
  `${app.toLowerCase()}/${platform.toLowerCase()}`;

export const config_key = (app: AppClient, platform: AppPlatform): string => `${app}:${platform}`;

/* ------------------------------ how it reads ------------------------------ */

export type StatusTone = 'blocking' | 'nudging' | 'quiet';

export const STATUS_LABEL: Record<UpdateStatus, string> = {
  FORCED: 'Blocked',
  OPTIONAL: 'Nudged',
  NONE: 'Allowed in',
};

export const STATUS_TONE: Record<UpdateStatus, StatusTone> = {
  FORCED: 'blocking',
  OPTIONAL: 'nudging',
  NONE: 'quiet',
};

/**
 * Colour for a status.
 *
 * FORCED is destructive rather than merely "warning": it is the one outcome that stops a
 * person using the app, and an operator scanning this screen needs it to look like what
 * it is.
 */
export const STATUS_CLASS: Record<UpdateStatus, string> = {
  FORCED: 'text-destructive',
  OPTIONAL: 'text-warning',
  NONE: 'text-muted-foreground',
};

/**
 * Whether a row is currently blocking anyone at all.
 *
 * `minimum_build` of 1 is the seeded default and refuses nobody, so it reads as "armed:
 * no" rather than as a number an operator has to interpret.
 */
export const is_armed = (c: AppConfig): boolean => c.minimum_build > 1;

/**
 * A row's gate in one sentence.
 *
 * Spelled out rather than shown as two numbers side by side, because "minimum 140, latest
 * 160" requires the reader to remember which way round the comparison goes, and getting it
 * backwards here locks people out of the app.
 */
export function gate_sentence(c: AppConfig): string {
  if (!is_armed(c)) {
    return c.latest_build > 1
      ? `Nobody is blocked. Builds below ${c.latest_build} see an optional nudge.`
      : 'Nobody is blocked and nobody is nudged.';
  }
  const nudged =
    c.latest_build > c.minimum_build
      ? ` Builds ${c.minimum_build}–${c.latest_build - 1} see a nudge they can skip.`
      : '';
  return `Builds below ${c.minimum_build} are blocked from using the app.${nudged}`;
}

/**
 * What the server would answer for a build, without asking it.
 *
 * Used ONLY for the unsaved-change warning, where there is no saved row to query yet. The
 * tester on this page deliberately calls the real endpoint instead of this — a mirror of
 * the server's rule is a second copy that can drift, and the whole point of that panel is
 * to show what a phone actually receives.
 */
export function predict_status(build: number, minimum_build: number, latest_build: number): UpdateStatus {
  if (build < minimum_build) return 'FORCED';
  if (build < latest_build) return 'OPTIONAL';
  return 'NONE';
}
